import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames/bind";
import FocusTrap from "focus-trap-react";
import type { KeyboardEvent } from "react";
import React, { useCallback, useEffect, useState } from "react";
import Button from "../Button/Button";
import { Portal } from "../Portal";
import styles from "./Dialog.module.scss";
import type { DialogProps } from "./types";

const cx = classNames.bind(styles);
const ANIMATION_DURATION = 150;

/**
 * A flexible dialog component
 */
const Dialog = ({
  isOpen,
  title,
  children,
  actionRow,
  onClose,
  onCancel,
  className,
  dialogClassName,
  hideCloseButton,
}: DialogProps) => {
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [dialogHeight, setDialogHeight] = useState(0);
  const observer = React.useRef<ResizeObserver>(null);

  if (observer.current === null) {
    // @ts-expect-error: Generally, rewriting current in this fashion is not allowed, but there are specific cases where it is proper (https://beta.reactjs.org/apis/react/useRef#avoiding-recreating-the-ref-contents)
    observer.current = new ResizeObserver((entries) => {
      if (entries?.[0]) {
        setDialogHeight((entries[0].target as HTMLElement).offsetHeight);
      }
    });
  }

  const [dialogComponent, setDialogComponent] =
    useState<HTMLDialogElement | null>(null);

  const dialogRef = useCallback(
    (node) => {
      if (node && observer?.current) {
        setDialogComponent(node);

        setDialogHeight(node.offsetHeight);

        observer.current.observe(node);
      }
    },
    [setDialogComponent, setDialogHeight, observer]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [dialogComponent]
  );

  const screenHeight = window.innerHeight;

  useEffect(() => {
    setDialogHeight(dialogComponent?.getBoundingClientRect()?.height ?? 0);
  }, [dialogComponent]);

  const [dialogAnimationStyle, api] = useSpring(() => ({
    config: { duration: ANIMATION_DURATION },
    height: screenHeight / 2 - dialogHeight / 2,
    opacity: 0.5,
  }));

  const [backdropStyle, backdropAPI] = useSpring(() => ({
    config: { duration: ANIMATION_DURATION },
    opacity: 0,
  }));

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    } else {
      setTimeout(() => {
        setIsMounted(false);
      }, ANIMATION_DURATION);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      // IMPROVE: involves looking into react spring typing
      // @ts-expect-error: look into react spring typing
      api.start({ opacity: 0, top: screenHeight / 2 - dialogHeight / 2 - 100 });

      backdropAPI.start({ opacity: 0 });
    } else {
      // IMPROVE: involves looking into react spring typing
      // @ts-expect-error: look into react spring typing
      api.start({ opacity: 1, top: screenHeight / 2 - dialogHeight / 2 });

      backdropAPI.start({ opacity: 1 });
    }
  }, [isOpen, dialogHeight, screenHeight]);

  return isMounted ? (
    <Portal>
      <div className={cx("container", className)}>
        <animated.div
          className={cx("backdrop")}
          style={backdropStyle}
        />

        <FocusTrap
          focusTrapOptions={{
            clickOutsideDeactivates: true,
          }}
        >
          <animated.div
            className={cx("position")}
            style={dialogAnimationStyle}
          >
            <div
              className={cx("dialog", { isOpen }, dialogClassName)}
              ref={dialogRef}
              onKeyDown={onKeyDown}
              role="dialog"
              aria-modal="true"
              aria-label={title}
            >
              <div className={cx("header")}>
                <h1 className={cx("title")}>{title}</h1>

                <Button
                  icon="x"
                  variant="ghost"
                  onClick={onClose}
                  style={{
                    opacity: hideCloseButton ? "0" : "1",
                    cursor: hideCloseButton ? "default" : "pointer",
                  }}
                />
              </div>

              <div className={cx("content")}>{children}</div>

              {actionRow ? (
                <div className={cx("footer")}>
                  {actionRow}
                  <button
                    className={cx("cancelButton")}
                    onClick={() => {
                      onCancel?.();
                      onClose();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}
            </div>
          </animated.div>
        </FocusTrap>
      </div>
    </Portal>
  ) : null;
};

export default Dialog;
